import React from 'react';
import { StarIcon } from '@heroicons/react/24/solid';

interface TestimonialProps {
  name: string;
  business: string;
  location: string;
  rating: number;
  testimonial: string;
  image?: string;
}

const TestimonialCard: React.FC<TestimonialProps> = ({ 
  name, 
  business, 
  location, 
  rating, 
  testimonial, 
  image 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center mb-4">
        <div className="flex-shrink-0">
          {image ? (
            <img 
              src={image} 
              alt={name} 
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {name.charAt(0)}
              </span>
            </div>
          )}
        </div>
        <div className="ml-4">
          <h4 className="font-semibold text-gray-900">{name}</h4>
          <p className="text-sm text-gray-600">{business}</p>
          <p className="text-sm text-gray-500">{location}</p>
        </div>
      </div>
      
      <div className="flex items-center mb-3">
        {[...Array(5)].map((_, i) => (
          <StarIcon
            key={i}
            className={`h-5 w-5 ${
              i < rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
      
      <blockquote className="text-gray-700 italic">
        "{testimonial}"
      </blockquote>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  const testimonials: TestimonialProps[] = [
    {
      name: "Sarah Johnson",
      business: "Johnson Family Apiary",
      location: "Georgia",
      rating: 5,
      testimonial: "Southern Bee Network connected me with amazing nucleus colonies from verified sellers. The premium seller badges gave me confidence in my purchases. My hives are thriving!"
    },
    {
      name: "Mike Rodriguez",
      business: "Honey Creek Farm",
      location: "Texas",
      rating: 5,
      testimonial: "As a commercial beekeeper, the business plan has been invaluable. The analytics help me track which products sell best, and the priority placement brings in consistent customers."
    },
    {
      name: "Jennifer Lee",
      business: "Mountain View Bees",
      location: "North Carolina",
      rating: 5,
      testimonial: "Started with the free plan and upgraded to premium after seeing the results. The customer reviews feature has built trust with buyers, and I've doubled my sales this season."
    },
    {
      name: "Robert Thompson",
      business: "Sweet South Honey Co.",
      location: "Alabama",
      rating: 5,
      testimonial: "The network has revolutionized how I find quality bee equipment and colonies. The verification system ensures I'm dealing with legitimate beekeepers every time."
    },
    {
      name: "Lisa Martinez",
      business: "Sunshine Apiary",
      location: "Florida",
      rating: 5,
      testimonial: "Love the map feature! I can see all the sellers in my area and read reviews before visiting. It's saved me countless hours and helped me find the best local suppliers."
    },
    {
      name: "David Wilson",
      business: "Wilson's Bee Supply",
      location: "South Carolina",
      rating: 5,
      testimonial: "The advertising opportunities have brought new customers to my business. The platform is professional, easy to use, and the customer service is outstanding."
    }
  ];

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            What Our Community Says
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of satisfied beekeepers who have found success through our network.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} />
          ))}
        </div>
        
        <div className="text-center mt-12">
          <div className="bg-yellow-50 rounded-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Join Our Growing Community?
            </h3>
            <p className="text-gray-700 mb-6">
              Over 5,000+ beekeepers trust Southern Bee Network to connect, buy, and sell with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors">
                Start Selling Today
              </button>
              <button className="border-2 border-yellow-500 text-yellow-600 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-50 transition-colors">
                Browse Sellers
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;